import { useState } from 'react'
import './ReactArticleCarousel.scss'

import { Modal, ModalTypes } from '@cox/core-ui8/dist/Modal'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'
import { useWindowSize } from '@cox/core-ui8/dist/useWindowSize'

import ReactArticleCarouselSkeleton from './ReactArticleCarouselSkeleton'
import { CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON, CAROUSEL_INACTIVE_ARROW_BUTTON, YOUTUBE_ICON } from '../../../assets/images'
import { DynamicMediaComponents, getDynamicMediaFromSrc } from '../../../utils/dynamic-media-util'
import { getImageSrc } from '../../../utils/local-util'
import {
  getAttributeValue,
  getConditionResult,
  getFlexOffersLength,
  hasCFConditionals
} from '../../../utils/rule-builder-util'
import { ContentType, RuleType } from '../../../utils/types'
import { getVideoThumbnail, playIcon } from '../../../utils/video-util'
import ReactBadge from '../../ReactBadge'
import ReactImage from '../../ReactImage'
import ReactText from '../../ReactText'
import ReactVideo from '../../ReactVideo'
import { LayoutType } from '../../ReactVideo/types'
import ReactImageOverlay from '../../shared/ReactImageOverlay'
import ActionButton from '../ActionButton'
import CustomDot from '../CustomDot'
import ReactLink from '../ReactLink'
import ReactMultiCarouselV2 from '../ReactMultiCarouselV2'
import {
  ActionButtonTypes,
  IArticleContentFragment,
  IArticleContentFragmentElements,
  responsiveCarouselProps,
  SourceTypes
} from '../types'

const ReactArticleCarousel = (props: any): JSX.Element => {
  const {
    ruleExpression = '',
    condition,
    ruleType,
    tokenProperties,
    videoIcon = YOUTUBE_ICON,
    resourceType = SourceTypes.IMAGE,
    youtubeAspectRatio = '',
    videoAccessibilityLabel = '',
    youtubeLoop = false,
    youtubePlaysInline = false,
    youtubeRel = false,
    modalHeader = ' ',
    contentFragments
  } = props
  const flexOffers = useFlexOffers(ruleExpression)
  const [modalVideoData, setModalVideoData] = useState({
    videoId: ''
  })
  function getCarouselItems() {
    const flexOffersLength = getFlexOffersLength(flexOffers)
    const carouselItems = contentFragments?.map((cf: IArticleContentFragment, index: any) => {
      //flex offers loading -- placeholders
      if (flexOffers?.loading) {
        return <ReactArticleCarouselSkeleton key={`${cf.id}_article`} />
      } else if (
        (index < flexOffersLength && props.contentType === ContentType.dynamic) ||
        props.contentType === ContentType.static
      ) {
        return (
          <ArticleCarouselSlide
            key={`${cf.id}_article`}
            index={index}
            ruleExpression={ruleExpression}
            condition={condition}
            ruleType={ruleType}
            tokenProperties={tokenProperties}
            resourceType={resourceType}
            {...cf.elements}
            videoIcon={videoIcon}
            handleVideoSelection={setModalVideoData}
          />
        )
      }
      return null
    })
    //filters out any empty values
    return carouselItems?.filter((elm: any) => elm)
  }

  const carouselItems = getCarouselItems()
  const {
    eyebrow,
    title,
    description,
    activePip,
    inActivePip,
    activePipAltText,
    inActivePipAltText,
    activeAuthoredIndex,
    carouselDisabledButton,
    carouselActiveButton,
    carouselActiveButtonAltText = 'Next',
    carouselDisabledButtonAltText = 'Previous'
  } = props

  return (
    <div className='react-carousel responsive-size' data-testid='react-carousel-article'>
      <ReactMultiCarouselV2
        //custom props
        eyebrow={eyebrow}
        title={title}
        description={description}
        //multicarousel props
        ssr={true}
        partialVisible={true}
        responsive={responsiveCarouselProps}
        arrows={false}
        renderButtonGroupOutside={true}
        customButtonGroup={
          <ButtonGroup
            nextButton={carouselActiveButton}
            previousButton={carouselDisabledButton}
            nextButtonAltText={carouselActiveButtonAltText}
            previousButtonAltText={carouselDisabledButtonAltText}
          />
        }
        showDots={true}
        renderDotsOutside={true}
        swipeable={true}
        customDot={
          <CustomDot
            activePip={activePip}
            inActivePip={inActivePip}
            activePipAltText={activePipAltText}
            inActivePipAltText={inActivePipAltText}
          />
        }
        flexOffers={flexOffers}
        activeAuthoredIndex={activeAuthoredIndex}
      >
        {carouselItems?.length > 0 &&
          carouselItems?.map((carouselItem: any) => {
            return (
              <div className='carousel-item-wrapper' key={JSON.stringify(carouselItem.key)}>
                {carouselItem}
              </div>
            )
          })}
      </ReactMultiCarouselV2>
      <Modal
        modalId={modalVideoData.videoId}
        show={modalVideoData.videoId != ''}
        componentName={'article-video-carousel'}
        title={modalHeader}
        handleClose={() =>
          setModalVideoData({
            videoId: ''
          })
        }
        modalType={ModalTypes.video}
        showFooter={false}
      >
        <ReactVideo
          youtubeVideoId={modalVideoData.videoId}
          isInEditor={false}
          layout={LayoutType.RESPONSIVE}
          autoplay={true}
          youtubeAspectRatio={youtubeAspectRatio}
          accessibilityLabel={videoAccessibilityLabel}
          videoIcon={videoIcon}
          youtubeLoop={youtubeLoop}
          youtubePlaysInline={youtubePlaysInline}
          youtubeRel={youtubeRel}
        />
      </Modal>
    </div>
  )
}

const ArticleCarouselSlide = (props: IArticleContentFragmentElements): JSX.Element => {
  const {
    image,
    imageParams,
    altImage,
    heading,
    contentBadgeColor,
    contentBadgeText,
    description,
    linkText,
    linkTextIcon,
    linkIconAltText,
    index = 0,
    ruleExpression = '',
    condition,
    ruleType,
    tokenProperties,
    linkUrl,
    videoId,
    videoAltTxt = '',
    resourceType,
    videoIcon,
    handleVideoSelection
  } = props

  const flexOffers = useFlexOffers(ruleExpression)

  const windowSize = useWindowSize()

  // get value if it is dynamic, or else return value authored value
  const getValue = (attribute: any, propertyName?: string) => {
    if (hasCFConditionals(attribute)) {
      const jsonAttribute = (props as any)?.[`${propertyName}_json`]
      return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName)
    }
    return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
  }
  // flex offers success
  let conditionResult = true
  if (flexOffers?.success && ruleType === RuleType.conditional && condition) {
    conditionResult = getConditionResult(condition, flexOffers)
  }
  if (conditionResult) {
    const contentBadgeTextValue = getValue(contentBadgeText, 'contentBadgeText')
    const contentBadgeColorValue = getValue(contentBadgeColor, 'contentBadgeColor')
    const videoAltTextValue = getValue(videoAltTxt, 'videoAltTxt')
    const altImageValue = getValue(altImage, 'altImage')
    const imageParamsValue = getValue(imageParams, 'imageParams')
    const imageValue = getDynamicMediaFromSrc(
      getValue(image, 'image') as string,
      (imageParamsValue as DynamicMediaComponents) || DynamicMediaComponents.CAROUSEL_ARTICLE
    )
    const videoValue = getValue(videoId, 'videoId')
    const linkTextValue = getValue(linkText, 'linkText')
    const linkTextIconValue = getValue(linkTextIcon, 'linkTextIcon')
    const linkIconAltTextValue = getValue(linkIconAltText, 'linkIconAltText')
    const linkUrlValue = getValue(linkUrl, 'linkUrl')
    const headingValue = getValue(heading, 'heading')
    const descriptionValue = getValue(description, 'description')
    const haslinkTextIcon = linkTextIcon?.value || linkIconAltText?.value

    const openThumbnail = (videoId: string) => {
      handleVideoSelection({ videoId })
      // if (videoId) {
      //   //this is to handle the scroll back to the top on the click of the modal.
      //   //with this it will get back to the position of navigation buttons on smooth scroll.
      //   const videoContainer = document.querySelector(`.vid${videoId}`)
      //   setTimeout(function () {
      //     videoContainer?.scrollIntoView({ behavior: 'auto' })
      //   }, 50)
      // }
    }

    const getImageContent = () => {
      if (imageValue) {
        return (
          <div key='image' className='image'>
            <ReactImage alt={altImageValue} src={imageValue} />
          </div>
        )
      }

      return <div key='image-placeholder' className='image-placeholder' />
    }

    const getVideoContent = () => {
      if (videoValue) {
        return (
          <ReactImageOverlay
            onClick={() => openThumbnail(videoValue)}
            additionalClass={`vid${videoValue}`}
            alt={videoAltTextValue}
            overlay={playIcon(windowSize.width, windowSize.height, videoIcon)}
          >
            <ReactImage src={getVideoThumbnail(videoValue)} alt='Play' />
          </ReactImageOverlay>
        )
      }

      return <div key='image-placeholder' className='image-placeholder' />
    }

    return (
      <div className='article'>
        <div className='article-container responsive-size' data-testid='article-container'>
          <div key='image-container' className='image-container'>
            {resourceType === SourceTypes.VIDEO && getVideoContent()}
            {resourceType === SourceTypes.IMAGE && getImageContent()}
            {contentBadgeTextValue && (
              <div key='badge' className='badge'>
                <ReactBadge
                  contentBadgeText={contentBadgeTextValue}
                  contentBadgeColor={contentBadgeColorValue}
                  isInContainer={true}
                />
              </div>
            )}
            {headingValue && <ReactText text={headingValue} isParsed className='heading' />}
          </div>

          {descriptionValue && <ReactText text={descriptionValue} isParsed className='headingDescription' />}
          <ReactLink
            linkTextValue={linkTextValue}
            linkUrlValue={linkUrlValue}
            linkTextIconValue={getImageSrc(linkTextIconValue)}
            linkIconAltTextValue={linkIconAltTextValue}
            haslinkTextIcon={haslinkTextIcon}
            iconClickable={true}
          />
        </div>
      </div>
    )
  }
  return <></>
}

const ButtonGroup = (props: any) => {
  const {
    carouselState: { currentSlide, totalItems },
    next,
    previous,
    nextButton = CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON,
    previousButton = CAROUSEL_INACTIVE_ARROW_BUTTON,
    nextButtonAltText,
    previousButtonAltText
  } = props
  return (
    <div className='carousel-button-group'>
      <ActionButton
        buttonType={ActionButtonTypes.PREVIOUS}
        isDisabled={currentSlide === 0}
        type='button'
        onClick={() => previous()}
        aria-label={previousButtonAltText}
        aria-disabled={currentSlide === 0}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
      />
      <ActionButton
        buttonType={ActionButtonTypes.NEXT}
        isDisabled={currentSlide === totalItems - 2}
        type='button'
        onClick={() => next()}
        aria-label={nextButtonAltText}
        aria-disabled={currentSlide === totalItems - 2}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
      />
    </div>
  )
}

export default ReactArticleCarousel
